/**
 * BrainSessionsStore — web mirror of `Sources/Store/BrainSessionsStore.swift`
 * (LIVE path).
 *
 * The real tmux client. Talks to the Brain Worker's OWNER-ONLY `GET /tmux` REST
 * endpoint (docs/BRAIN_API.md), which dispatches to the operator's Mac bridge.
 * Because the work runs on the Mac via the bridge, this REQUIRES the engine online
 * and can long-poll for up to ~25s or time out with 504.
 *
 * AUTH: `Authorization: Bearer brain_*` — the SAME durable token BrainAuth holds
 * in localStorage (NOT the short-lived Supabase JWT). Status codes are the same
 * owner-only set as /files (401/403/502/503/504); non-2xx surfaces as
 * BrainFsError(status) so the view can render the right copy.
 */

import type { BrainConfig } from '../config';
import { BrainFsError, decodeTmuxList, type TmuxListResponse } from '../models/brainFs';
import type { SessionsStore } from './SessionsStore';

export class BrainSessionsStore implements SessionsStore {
  private readonly baseURL: string;
  private readonly brainToken: () => Promise<string | null>;

  constructor(config: BrainConfig, brainToken: () => Promise<string | null>) {
    this.baseURL = config.brainBaseURL;
    this.brainToken = brainToken;
  }

  async list(): Promise<TmuxListResponse> {
    const token = await this.brainToken();
    if (!token) throw new BrainFsError('No Brain token', 401);

    const url = new URL('/tmux', this.baseURL);

    let res: Response;
    try {
      res = await fetch(url.toString(), {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
    } catch (e) {
      // Transport failure — never reached the engine; treat as offline (504).
      throw new BrainFsError(e instanceof Error ? e.message : 'Network error', 504);
    }

    const text = await res.text();
    if (!res.ok) {
      throw new BrainFsError(`Brain returned HTTP ${res.status}`, res.status);
    }
    try {
      return decodeTmuxList(JSON.parse(text));
    } catch {
      throw new BrainFsError('Unexpected Brain response: not JSON');
    }
  }
}
